import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Prospects, Guests as GuestsApi } from '../../lib/api';
import { SOURCES, PROSPECT_STATUSES, PROSPECT_LABELS } from '../../lib/prospectConfig';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Plus, Mail, Loader2, Users, UserPlus, Inbox } from 'lucide-react';
import NewProspectDialog from '../NewProspectDialog';
import ProspectDetailSheet from '../ProspectDetailSheet';
import InboxSweepDialog from '../InboxSweepDialog';
import { toast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';

const POTENTIAL_OPT = { value: 'potential', label: 'Potential Guest', color: '#A78BFA' };

function Pill({ value, options }) {
  const opt = options.find(o => o.value === value) || options[0];
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: `${opt.color}22`, color: opt.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: opt.color }} />
      {opt.label}
    </span>
  );
}

export default function ProspectsView({ onSelectGuest }) {
  const { activeShow, activeShowId, guests, refreshGuests } = useApp();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [newOpen, setNewOpen] = useState(false);
  const [sweepOpen, setSweepOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [promotingId, setPromotingId] = useState(null);

  const load = (initial = false) => {
    if (initial) setItems(null);
    Prospects.list(activeShowId).then(setItems);
  };
  useEffect(() => { load(true); }, [activeShowId]); // eslint-disable-line

  if (items === null) return <div className="h-full grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  // One unified pipeline: potential guests (from the Guests table) + prospects
  const potentials = guests.filter(g => g.showId === activeShowId && g.status === 'potential');
  const combined = [
    ...potentials.map(g => ({ kind: 'potential', labels: ['podcast_guest'], ...g })),
    ...items.map(p => ({ kind: 'prospect', ...p, labels: p.labels?.length ? p.labels : ['podcast_guest'] })),
  ];

  const byType = typeFilter === 'all' ? combined : combined.filter(r => (r.labels || []).includes(typeFilter));

  const filtered = filter === 'all'
    ? byType
    : filter === 'potential'
      ? byType.filter(r => r.kind === 'potential')
      : byType.filter(r => r.kind === 'prospect' && r.status === filter);

  const counts = items.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});
  const newCount = counts['new'] || 0;
  const contactedCount = counts['contacted'] || 0;
  const bookedCount = counts['booked'] || 0;

  const promote = async (g) => {
    setPromotingId(g.id);
    try {
      await GuestsApi.promoteToProspect(g.id, { source: 'other', sourceDetail: 'Promoted from Potential Guest' });
      toast({ title: 'Promoted to Prospect', description: `${g.name} is now in the outreach pipeline.` });
      await refreshGuests();
      load();
    } catch (e) {
      toast({ title: 'Promotion failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setPromotingId(null);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-4 md:px-5 py-4 border-b border-stone-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-serif text-stone-900">Prospects</h1>
          <div className="text-[12px] text-stone-500">
            {combined.length} in pipeline • {potentials.length} potential • {newCount} new • {contactedCount} contacted • {bookedCount} booked
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="find-lost-guests-btn" onClick={() => setSweepOpen(true)}>
            <Inbox className="w-3.5 h-3.5 mr-1" /> Find lost guests
          </Button>
          <Button onClick={() => setNewOpen(true)} className="text-white" style={{ background: activeShow.color }}><Plus className="w-3.5 h-3.5 mr-1" /> Add prospect</Button>
        </div>
      </div>

      <div className="px-5 py-2 border-b border-stone-100 flex items-center gap-1.5 overflow-x-auto">
        {[{ value: 'all', label: 'All' }, POTENTIAL_OPT, ...PROSPECT_STATUSES].map(opt => {
          const c = opt.value === 'all' ? byType.length : opt.value === 'potential' ? byType.filter(r => r.kind === 'potential').length : byType.filter(r => r.kind === 'prospect' && r.status === opt.value).length;
          const active = filter === opt.value;
          return (
            <button key={opt.value} data-testid={`pipeline-filter-${opt.value}`} onClick={() => setFilter(opt.value)} className={cn('px-2.5 py-1 rounded-md text-[12px] flex items-center gap-1.5', active ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-100')}>
              {opt.label} <span className={cn('text-[10.5px]', active ? 'text-stone-300' : 'text-stone-400')}>{c}</span>
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-1 pl-3 border-l border-stone-200 shrink-0">
          <span className="text-[10.5px] uppercase tracking-wider text-stone-400 mr-1">Type</span>
          {[{ value: 'all', label: 'All' }, ...PROSPECT_LABELS].map(opt => {
            const c = opt.value === 'all' ? combined.length : combined.filter(r => (r.labels || []).includes(opt.value)).length;
            const active = typeFilter === opt.value;
            return (
              <button key={opt.value} data-testid={`pipeline-type-filter-${opt.value}`} onClick={() => setTypeFilter(opt.value)} className={cn('px-2.5 py-1 rounded-md text-[12px] flex items-center gap-1.5 whitespace-nowrap', active ? 'text-white' : 'text-stone-700 hover:bg-stone-100')} style={active ? { background: opt.color || '#1c1917' } : undefined}>
                {opt.label} <span className={cn('text-[10.5px]', active ? 'text-white/70' : 'text-stone-400')}>{c}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {filtered.length === 0 ? (
          <Card className="m-8 border-stone-200"><CardContent className="p-10 text-center text-stone-500">
            <Users className="w-8 h-8 mx-auto mb-2 text-stone-300" />
            <div className="text-[14px] mb-1">Nothing here yet</div>
            <div className="text-[12px]">Click “Add prospect”, or mark a guest as “Potential Guest” and they will appear here too.</div>
          </CardContent></Card>
        ) : (
          <table className="min-w-full text-[13px]">
            <thead className="sticky top-0 bg-stone-50 border-b border-stone-200 z-10">
              <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500">
                <th className="px-4 py-2.5 font-semibold">Name</th>
                <th className="px-4 py-2.5 font-semibold">Source</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Last contact</th>
                <th className="px-4 py-2.5 font-semibold">Email</th>
                <th className="px-4 py-2.5 font-semibold w-36">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr
                  key={`${r.kind}-${r.id}`}
                  data-testid={`pipeline-row-${r.kind}-${r.id}`}
                  className={cn('border-b border-stone-100 hover:bg-stone-50 cursor-pointer', i % 2 === 0 ? 'bg-white' : 'bg-stone-50/30')}
                  onClick={() => r.kind === 'potential' ? onSelectGuest?.(r.id) : setSelected(r.id)}
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-stone-900 truncate">{r.name}</span>
                      {(r.labels || []).includes('app_user') && (
                        <span data-testid={`label-badge-app-user-${r.id}`} className="inline-flex px-1.5 py-px rounded text-[9.5px] font-semibold uppercase tracking-wide shrink-0" style={{ background: '#0EA5E922', color: '#0284C7' }}>App User</span>
                      )}
                    </div>
                    <div className="text-[11px] text-stone-500 truncate">{r.role}{r.role && r.company ? ' • ' : ''}{r.company}</div>
                  </td>
                  <td className="px-4 py-2.5">{r.kind === 'prospect' ? <Pill value={r.source} options={SOURCES} /> : <span className="text-[11px] text-stone-400">—</span>}</td>
                  <td className="px-4 py-2.5">
                    {r.kind === 'potential'
                      ? <Pill value="potential" options={[POTENTIAL_OPT]} />
                      : <Pill value={r.status} options={PROSPECT_STATUSES} />}
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-stone-600">{r.lastContactedAt ? new Date(r.lastContactedAt).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-2.5 text-[12px] text-stone-600 truncate">{r.email}</td>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    {r.kind === 'potential' ? (
                      <Button size="sm" variant="outline" data-testid={`promote-row-${r.id}`} disabled={promotingId === r.id} onClick={() => promote(r)} className="border-violet-300 text-violet-700 hover:bg-violet-50">
                        {promotingId === r.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />} Promote
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setSelected(r.id)}><Mail className="w-3.5 h-3.5 mr-1" /> Reach out</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewProspectDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
      <InboxSweepDialog open={sweepOpen} onOpenChange={setSweepOpen} onAdded={() => { load(); refreshGuests(); }} onOpenProspect={(id) => setSelected(id)} />
      <ProspectDetailSheet prospectId={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} onChanged={load} />
    </div>
  );
}
